import type { CarnivalGameState, Die, RoundSummary } from "../types";

export function diceSumForOwner(dice: readonly Die[], ownerId: string): number {
  return dice.reduce(
    (sum, die) => (die.lockedBy?.ownerId === ownerId ? sum + die.value : sum),
    0,
  );
}

/**
 * This round's score for `playerId` before the white die bonus. Envy copies
 * the devil card holder's own dice sum — including when the devil card
 * holder plays Envy on themselves, which naturally resolves to 0 since an
 * Envy card never locks any dice to its owner.
 */
export function computeRawRoundScore(
  state: CarnivalGameState,
  playerId: string,
): number {
  const revealed = state.revealedThisRound.find(
    (record) => record.playerId === playerId,
  );
  if (revealed?.sin === "envy") {
    return diceSumForOwner(state.dice, state.devilCardHolderId);
  }
  return diceSumForOwner(state.dice, playerId);
}

function findRevealIndex(state: CarnivalGameState, playerId: string): number {
  const revealed = state.revealedThisRound.find(
    (record) => record.playerId === playerId,
  );
  if (!revealed)
    throw new Error(`Player ${playerId} has not revealed this round`);
  return revealed.revealIndex;
}

export function computeRoundSummary(state: CarnivalGameState): RoundSummary {
  const whiteDie = state.dice.find((die) => die.color === "white");
  if (!whiteDie) throw new Error("White die is missing from game state");

  const rawScoreByPlayerId: Record<string, number> = {};
  for (const player of state.players) {
    rawScoreByPlayerId[player.playerId] = computeRawRoundScore(
      state,
      player.playerId,
    );
  }

  const lowestRaw = Math.min(...Object.values(rawScoreByPlayerId));
  const lowestScorePlayerIds = state.players
    .map((player) => player.playerId)
    .filter((playerId) => rawScoreByPlayerId[playerId] === lowestRaw);

  const finalScoreByPlayerId: Record<string, number> = {};
  for (const player of state.players) {
    const isLowest = lowestScorePlayerIds.includes(player.playerId);
    finalScoreByPlayerId[player.playerId] =
      rawScoreByPlayerId[player.playerId] + (isLowest ? whiteDie.value : 0);
  }

  const prideRevealerIds = state.revealedThisRound
    .filter((record) => record.sin === "pride")
    .sort((a, b) => a.revealIndex - b.revealIndex)
    .map((record) => record.playerId);

  let nextDevilCardHolderId: string;
  if (prideRevealerIds.length > 0) {
    nextDevilCardHolderId = prideRevealerIds[prideRevealerIds.length - 1];
  } else {
    const lowestFinal = Math.min(...Object.values(finalScoreByPlayerId));
    const tiedForLowestFinal = state.players
      .map((player) => player.playerId)
      .filter((playerId) => finalScoreByPlayerId[playerId] === lowestFinal);
    nextDevilCardHolderId = tiedForLowestFinal.reduce((latest, candidate) =>
      findRevealIndex(state, candidate) > findRevealIndex(state, latest)
        ? candidate
        : latest,
    );
  }

  return {
    round: state.round,
    rawScoreByPlayerId,
    finalScoreByPlayerId,
    whiteDieValue: whiteDie.value,
    lowestScorePlayerIds,
    prideRevealerIds,
    nextDevilCardHolderId,
  };
}

export function computeTotalScore(
  state: CarnivalGameState,
  playerId: string,
): number {
  const history = state.scoreHistoryByPlayerId[playerId] ?? [];
  return history.reduce((sum, score) => sum + score, 0);
}

/**
 * Winner(s) after the final round: highest total score; ties broken by
 * highest single-round score; still-tied candidates are joint winners.
 */
export function determineWinners(state: CarnivalGameState): readonly string[] {
  const playerIds = state.players.map((player) => player.playerId);
  const totals = playerIds.map((playerId) =>
    computeTotalScore(state, playerId),
  );
  const maxTotal = Math.max(...totals);
  const totalCandidates = playerIds.filter(
    (playerId) => computeTotalScore(state, playerId) === maxTotal,
  );
  if (totalCandidates.length === 1) return totalCandidates;

  const bestRoundByCandidate = totalCandidates.map((playerId) =>
    Math.max(...(state.scoreHistoryByPlayerId[playerId] ?? [0])),
  );
  const maxBestRound = Math.max(...bestRoundByCandidate);
  return totalCandidates.filter(
    (_playerId, index) => bestRoundByCandidate[index] === maxBestRound,
  );
}
